import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { KeyRound, ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'

interface PasswordResetSectionProps {
  canResetPassword: boolean
  onPasswordChange: (password: string | undefined) => void
  className?: string
}

export function PasswordResetSection({
  canResetPassword,
  onPasswordChange,
  className,
}: PasswordResetSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({})

  if (!canResetPassword) {
    return null
  }

  const validatePasswords = (newPwd: string, confirmPwd: string) => {
    const newErrors: { newPassword?: string; confirmPassword?: string } = {}

    if (newPwd && newPwd.length < 6) {
      newErrors.newPassword = '密碼至少需要 6 個字元'
    }

    if (newPwd && confirmPwd && newPwd !== confirmPwd) {
      newErrors.confirmPassword = '確認密碼不一致'
    }

    setErrors(newErrors)

    // If both fields are empty, return undefined (no password change)
    if (!newPwd && !confirmPwd) {
      onPasswordChange(undefined)
      return
    }

    // If valid, pass the password
    if (newPwd && newPwd.length >= 6 && newPwd === confirmPwd) {
      onPasswordChange(newPwd)
    } else {
      onPasswordChange(undefined)
    }
  }

  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value)
    validatePasswords(value, confirmPassword)
  }

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value)
    validatePasswords(newPassword, value)
  }

  const handleToggle = () => {
    if (isExpanded) {
      // Collapse and reset
      setNewPassword('')
      setConfirmPassword('')
      setErrors({})
      onPasswordChange(undefined)
    }
    setIsExpanded(!isExpanded)
  }

  return (
    <div className={cn('space-y-3', className)}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleToggle}
        className="w-full justify-between"
      >
        <span className="flex items-center gap-2">
          <KeyRound className="h-4 w-4" />
          重設密碼
        </span>
        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </Button>

      {isExpanded && (
        <div className="space-y-3 rounded-lg border p-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">新密碼</Label>
            <Input
              id="new-password"
              type="password"
              placeholder="請輸入新密碼（至少 6 個字元）"
              value={newPassword}
              onChange={(e) => handleNewPasswordChange(e.target.value)}
            />
            {errors.newPassword && (
              <p className="text-sm text-destructive">{errors.newPassword}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">確認密碼</Label>
            <Input
              id="confirm-password"
              type="password"
              placeholder="請再次輸入新密碼"
              value={confirmPassword}
              onChange={(e) => handleConfirmPasswordChange(e.target.value)}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword}</p>
            )}
          </div>

          {newPassword && confirmPassword && !errors.newPassword && !errors.confirmPassword && (
            <p className="text-sm text-green-600">密碼驗證通過</p>
          )}
        </div>
      )}
    </div>
  )
}
